import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FileText, ChevronLeft, ChevronRight } from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";
import { formatQuestionType } from "@/utils/formatters";

type CandidateResponse = Tables<"candidate_responses">;

interface CandidateAssessmentAnswersProps {
  candidateId: string;
}

export function CandidateAssessmentAnswers({
  candidateId,
}: CandidateAssessmentAnswersProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [questionTypeFilter, setQuestionTypeFilter] = useState<string>("all");
  const [groupFilter, setGroupFilter] = useState<string>("all");
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const { data: responses = [], isLoading } = useQuery({
    queryKey: ["candidate-responses", candidateId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("candidate_responses")
        .select(
          `
          *,
          questions(
            question_text,
            question_type,
            options,
            correct_answer,
            score,
            question_groups(name)
          )
        `
        )
        .eq("candidate_id", candidateId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data || [];
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">Loading assessment answers...</div>
        </CardContent>
      </Card>
    );
  }

  if (responses.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Assessment Answers
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            No assessment answers available for this candidate.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Determine available types and groups based on current filters
  const baseForTypes =
    groupFilter === "all"
      ? responses
      : responses.filter(
          (r) => (r.questions as any)?.question_groups?.name === groupFilter
        );
  const questionTypes = [
    ...new Set(
      baseForTypes.map((r) => (r.questions as any)?.question_type).filter(Boolean)
    ),
  ];

  const baseForGroups =
    questionTypeFilter === "all"
      ? responses
      : responses.filter(
          (r) => (r.questions as any)?.question_type === questionTypeFilter
        );
  const groups = [
    ...new Set(
      baseForGroups
        .map((r) => (r.questions as any)?.question_groups?.name)
        .filter(Boolean)
    ),
  ];

  const ipCounts = responses.reduce<Record<string, number>>((acc, r) => {
    const ip = r.ip_address || "unknown";
    acc[ip] = (acc[ip] || 0) + 1;
    return acc;
  }, {});
  const hasMultipleIps = Object.keys(ipCounts).length > 1;

  // Filter responses based on selected type and group
  let filteredResponses = responses;
  if (questionTypeFilter !== "all") {
    filteredResponses = filteredResponses.filter(
      (r) => (r.questions as any)?.question_type === questionTypeFilter
    );
  }
  if (groupFilter !== "all") {
    filteredResponses = filteredResponses.filter(
      (r) => (r.questions as any)?.question_groups?.name === groupFilter
    );
  }

  // Paginate filtered responses
  const totalPages = Math.ceil(filteredResponses.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedResponses = filteredResponses.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Assessment Answers ({responses.length})
        </CardTitle>

        {/* Question Type Filter */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant={questionTypeFilter === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setQuestionTypeFilter("all");
              setCurrentPage(1);
            }}
          >
            All ({baseForTypes.length})
          </Button>
          {questionTypes.map((type) => {
            const count = baseForTypes.filter(
              (r) => (r.questions as any)?.question_type === type
            ).length;
            return (
              <Button
                key={type}
                variant={questionTypeFilter === type ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setQuestionTypeFilter(type);
                  setCurrentPage(1);
                }}
              >
                {formatQuestionType(type)} ({count})
              </Button>
            );
          })}
        </div>

        {/* Group Filter */}
        <div className="flex flex-wrap gap-2 mt-2">
          <Button
            variant={groupFilter === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setGroupFilter("all");
              setCurrentPage(1);
            }}
          >
            All Groups ({baseForGroups.length})
          </Button>
          {groups.map((group) => {
            const count = baseForGroups.filter(
              (r) => (r.questions as any)?.question_groups?.name === group
            ).length;
            return (
              <Button
                key={group}
                variant={groupFilter === group ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setGroupFilter(group);
                  setCurrentPage(1);
                }}
              >
                {group} ({count})
              </Button>
            );
          })}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {paginatedResponses.map((response, idx) => {
          const question = response.questions as any;
          const options =
            question?.options && typeof question.options === "object"
              ? Object.entries(question.options as Record<string, string>)
              : [];
          return (
            <div key={response.id} className="border rounded-lg p-4 space-y-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-1">
                  <h4 className="font-medium text-sm flex items-center gap-2">
                    <span className="text-muted-foreground">
                      #{startIndex + idx + 1}
                    </span>
                    <span className="prose-sm dark:prose-invert question-markdown-body">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>
                        {question?.question_text || ""}
                      </ReactMarkdown>
                    </span>
                  </h4>
                  <div className="flex gap-2 flex-wrap">
                    <Badge variant="outline" className="text-xs">
                      {formatQuestionType(question?.question_type)}
                    </Badge>
                    {typeof question?.score === "number" && (
                      <Badge variant="outline" className="text-xs">
                        Score: {question.score}
                      </Badge>
                    )}
                    {response.is_correct !== null && (
                      <Badge
                        variant={
                          response.is_correct ? "default" : "destructive"
                        }
                        className="text-xs"
                      >
                        {response.is_correct ? "Correct" : "Incorrect"}
                      </Badge>
                    )}
                    {response.ai_score && (
                      <Badge variant="secondary" className="text-xs">
                        AI Score: {response.ai_score}
                      </Badge>
                    )}
                  </div>
                </div>
                {hasMultipleIps && response.ip_address && (
                  <div className="text-xs text-red-600 ml-2 whitespace-nowrap">
                    IP: {response.ip_address}
                  </div>
                )}
              </div>

              {question?.question_type === "mcq" && (
                <div className="space-y-1">
                  {options.map(([key, value]) => (
                    <div
                      key={key}
                      className="flex items-start gap-2 p-2 border rounded"
                    >
                        <span className="font-medium">{key}.</span>
                        <div className="flex-1 prose-sm dark:prose-invert">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {value as any}
                          </ReactMarkdown>
                        </div>
                        {response.selected_option === key && (
                          <Badge variant="secondary" className="ml-2">Selected</Badge>
                        )}
                        {question?.correct_answer === key && (
                          <Badge variant="outline" className="ml-1">Correct</Badge>
                        )}
                      </div>
                    ))}
                </div>
              )}

              {question?.question_type === "open_ended" && (
                <div className="grid md:grid-cols-2 gap-2">
                  {response.response_text ? (
                    <div className="bg-gray-50 p-2 rounded text-sm">
                      <span className="font-medium">Answer:</span>
                      <div className="mt-1 prose-sm dark:prose-invert">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {response.response_text}
                        </ReactMarkdown>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 p-2 rounded text-sm">Not answered</div>
                  )}
                  {response.response_text && question?.correct_answer && (
                    <div className="bg-green-50 p-2 rounded text-sm">
                      <span className="font-medium">Correct:</span>
                      <div className="mt-1 prose-sm dark:prose-invert">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {question.correct_answer}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {response.ai_feedback && response.response_text && (
                <div className="bg-blue-50 p-2 rounded text-sm">
                  <span className="font-medium">AI Feedback:</span>
                  <div className="mt-1 prose-sm dark:prose-invert">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {response.ai_feedback}
                    </ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {hasMultipleIps && (
          <div className="text-sm text-red-600 border-t pt-2">
            <p className="font-medium">Multiple IP addresses detected:</p>
            <ul className="list-disc list-inside">
              {Object.entries(ipCounts).map(([ip, count]) => (
                <li key={ip}>
                  {ip} - {count} answers
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-sm text-muted-foreground">
              Showing {startIndex + 1}-
              {Math.min(startIndex + itemsPerPage, filteredResponses.length)} of{' '}
              {filteredResponses.length} answers
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm">Per page:</span>
              <Select
                value={itemsPerPage.toString()}
                onValueChange={(v) => {
                  setItemsPerPage(parseInt(v));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[5, 10, 20, 50].map((n) => (
                    <SelectItem key={n} value={n.toString()}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <span className="text-sm">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
